import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

/**
 * A custom hook to fetch financial report data for a tenant
 * @param {string} tenantId - The ID of the tenant
 * @returns {object} - Contains data, loading state, error state, and refetch function
 */
const useFinancialData = (tenantId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partialLoading, setPartialLoading] = useState(false);

  // Function to fetch financial data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getFinancialReport(tenantId);
      console.log("Données reçues du serveur:", response);
      
      // Extraire les données du wrapper API
      const extractedData = response.data;
      
      // Vérifier si les données contiennent bien un ID de locataire
      if (extractedData && extractedData.locataire) {
        console.log("ID du locataire reçu:", extractedData.locataire.id);
        
        if (!extractedData.locataire.id) {
          console.warn("ID du locataire manquant dans les données reçues");
          
          // Si l'ID est manquant mais qu'on a un ID de tenant, on peut l'utiliser comme fallback
          if (tenantId) {
            console.log("Utilisation de l'ID du tenant comme fallback:", tenantId);
            extractedData.locataire.id = tenantId;
          }
        }
      } else {
        console.error("Structure de données incorrecte:", extractedData);
      }
      
      setData(extractedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch financial data');
      console.error('Error fetching financial data:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch data on component mount or when tenantId changes
  useEffect(() => {
    if (tenantId) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
    }
  }, [tenantId, fetchData]);

  // Function for partial data refresh without full page loading
  const partialRefetch = async () => {
    setPartialLoading(true);
    try {
      const response = await apiService.getFinancialReport(tenantId);
      console.log("Données reçues du rafraîchissement partiel:", response);
      
      // Extraire les données du wrapper API
      const extractedData = response.data;
      
      // Vérifier si les données contiennent bien un ID de locataire
      if (extractedData && extractedData.locataire) {
        console.log("ID du locataire reçu:", extractedData.locataire.id);
        
        if (!extractedData.locataire.id && tenantId) {
          console.log("Utilisation de l'ID du tenant comme fallback:", tenantId);
          extractedData.locataire.id = tenantId;
        }
      }
      
      setData(extractedData);
    } catch (err) {
      console.error('Erreur lors du rafraîchissement partiel:', err);
      // Ne pas modifier l'état d'erreur global pour un rafraîchissement partiel
    } finally {
      // Petit délai pour assurer la visibilité de l'indicateur de chargement
      setTimeout(() => {
        setPartialLoading(false);
      }, 500);
    }
  };

  return { data, loading, error, partialLoading, refetch: fetchData, partialRefetch };
};

export default useFinancialData;
